import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Bed, Eye, EyeOff, Pencil, BookOpen, ExternalLink, Users } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useProducerAccommodations } from '@/hooks/useProducerAccommodations';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getOccupancy, getOccupancyDots } from '@/lib/booking/occupancy';
import { ProducerLayout } from '@/components/producer/ProducerLayout';

interface AccommodationBookingSummary {
  accommodation_id: string;
  upcoming_count: number;
  next_check_in: string | null;
  next_check_out: string | null;
}

function useAccommodationBookingSummaries(farmId: string | undefined) {
  return useQuery({
    queryKey: ['accommodation-booking-summaries', farmId],
    queryFn: async () => {
      if (!farmId) return { summaries: {}, monthBookings: {} };
      const today = new Date().toISOString().slice(0, 10);
      const monthStart = startOfMonth(new Date()).toISOString().slice(0, 10);
      const monthEnd = endOfMonth(new Date()).toISOString().slice(0, 10);

      // Fetch upcoming + current month bookings in one query
      const { data, error } = await supabase
        .from('accommodation_bookings')
        .select('accommodation_id, check_in, check_out, status')
        .eq('producer_id', farmId)
        .eq('status', 'confirmed')
        .or(`check_out.gte.${today},and(check_in.lte.${monthEnd},check_out.gte.${monthStart})`)
        .order('check_in', { ascending: true });
      if (error) throw error;

      const summaries: Record<string, AccommodationBookingSummary> = {};
      const monthBookings: Record<string, { check_in: string; check_out: string }[]> = {};

      for (const b of data ?? []) {
        // Upcoming summary
        if (b.check_out >= today) {
          if (!summaries[b.accommodation_id]) {
            summaries[b.accommodation_id] = {
              accommodation_id: b.accommodation_id,
              upcoming_count: 0,
              next_check_in: null,
              next_check_out: null,
            };
          }
          const s = summaries[b.accommodation_id];
          s.upcoming_count++;
          if (!s.next_check_in || b.check_in < s.next_check_in) {
            s.next_check_in = b.check_in;
            s.next_check_out = b.check_out;
          }
        }
        // Current month bookings for occupancy
        if (b.check_in <= monthEnd && b.check_out >= monthStart) {
          if (!monthBookings[b.accommodation_id]) {
            monthBookings[b.accommodation_id] = [];
          }
          monthBookings[b.accommodation_id].push({ check_in: b.check_in, check_out: b.check_out });
        }
      }
      return { summaries, monthBookings };
    },
    enabled: !!farmId,
  });
}

function formatPriceLabel(text: string | null | undefined): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (/kr|per|natt/i.test(trimmed)) return trimmed;
  if (/^\d+$/.test(trimmed)) return `Från ${trimmed} kr per natt`;
  return trimmed;
}

/** Cover image for an accommodation card */
function AccommodationCover({ accommodationId, title }: { accommodationId: string; title: string }) {
  const coverUrl = useCoverImageUrl('accommodation', accommodationId, 640);
  if (coverUrl) {
    return (
      <div className="aspect-[16/10] overflow-hidden">
        <img src={coverUrl} alt={title} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className="aspect-[16/10] bg-muted flex items-center justify-center">
      <Bed className="h-10 w-10 text-muted-foreground/30" />
    </div>
  );
}

export default function ProducerAccommodationsPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const { data: accommodations, isLoading } = useProducerAccommodations(farmId);
  const { data: bookingData } = useAccommodationBookingSummaries(farmId);
  const summaries = bookingData?.summaries ?? {};
  const monthBookings = bookingData?.monthBookings ?? {};

  if (isLoading) {
    return (
      <ProducerLayout farmId={farmId} title="Boende">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Boende</h1>
          </div>
          <p className="text-muted-foreground">Laddar...</p>
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout farmId={farmId} title="Boende">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Boende</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Hantera era boendealternativ och tillgänglighet
          </p>
        </div>
        <Button onClick={() => navigate('new')}>
          <Plus className="h-4 w-4 mr-2" />
          Nytt boende
        </Button>
      </div>

      {(!accommodations || accommodations.length === 0) ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bed className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-medium mb-2">Inga boenden ännu</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Lägg till ert första boende för att ta emot gäster
            </p>
            <Button onClick={() => navigate('new')}>
              <Plus className="h-4 w-4 mr-2" />
              Skapa boende
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {accommodations.map((acc) => {
            const summary = summaries[acc.id];
            const priceLabel = formatPriceLabel(acc.price_per_night_text);
            const accMonthBookings = monthBookings[acc.id] ?? [];
            const occupancy = getOccupancy(accMonthBookings);
            const dots = getOccupancyDots(occupancy.rate);
            return (
              <Card key={acc.id} className="overflow-hidden transition-shadow hover:shadow-md flex flex-col">
                {/* Cover image */}
                <AccommodationCover accommodationId={acc.id} title={acc.title} />

                <CardContent className="p-4 flex flex-col flex-1 space-y-4">
                  {/* Title + status */}
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-foreground line-clamp-1 text-base">{acc.title}</h3>
                    <Badge variant={acc.status === 'public' ? 'default' : 'secondary'} className="shrink-0 text-xs">
                      {acc.status === 'public' ? (
                        <><Eye className="h-3 w-3 mr-1" />Publicerad</>
                      ) : (
                        <><EyeOff className="h-3 w-3 mr-1" />Utkast</>
                      )}
                    </Badge>
                  </div>

                  {/* Price */}
                  {priceLabel ? (
                    <p className="text-sm font-medium text-foreground">{priceLabel}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Pris visas vid förfrågan</p>
                  )}

                  {/* Capacity */}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {acc.beds && (
                      <span className="flex items-center gap-1">
                        <Bed className="h-3 w-3" /> {acc.beds} bäddar
                      </span>
                    )}
                    {acc.guest_capacity && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" /> Max {acc.guest_capacity}
                      </span>
                    )}
                  </div>

                  {/* Booking signal — visually separated */}
                  <div className="border-t border-border pt-3 space-y-0.5">
                    {summary && summary.upcoming_count > 0 ? (
                      <>
                        <p className="text-xs text-muted-foreground">Nästa bokning</p>
                        {summary.next_check_in && summary.next_check_out && (
                          <p className="text-sm font-medium text-foreground">
                            {format(new Date(summary.next_check_in), 'd', { locale: sv })}–{format(new Date(summary.next_check_out), 'd MMM', { locale: sv })}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {summary.upcoming_count} kommande {summary.upcoming_count === 1 ? 'bokning' : 'bokningar'}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">Inga kommande bokningar</p>
                    )}
                  </div>

                  {/* Occupancy signal */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      Beläggning ({occupancy.monthLabel})
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <span
                            key={i}
                            className={`inline-block w-1.5 h-1.5 rounded-full ${
                              i < dots ? 'bg-primary' : 'bg-muted-foreground/20'
                            }`}
                          />
                        ))}
                      </span>
                      <span className="font-medium text-foreground">{occupancy.label}</span>
                    </span>
                  </div>

                  {/* Actions — pushed to bottom */}
                  <div className="flex flex-col gap-2 pt-1 mt-auto">
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/app/producer/farm/${farmId}/bookings?type=accommodation&accommodationId=${acc.id}`);
                      }}
                    >
                      <BookOpen className="h-3.5 w-3.5 mr-1.5" />
                      Se bokningar
                    </Button>
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${acc.id}/edit`);
                        }}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Redigera
                      </Button>
                      {acc.status === 'public' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/accommodation/${acc.id}`, '_blank');
                          }}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Visa
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </ProducerLayout>
  );
}
